import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { CalendarIcon, Upload, User, Home, Zap, Euro, FileText, Building2 } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";
import SirenSearch from "./SirenSearch";
import AddressSearch from "./AddressSearch";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SourceProspectSelect = ({ value, onChange }) => {
  const [sources, setSources] = React.useState([]);
  React.useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API_URL}/api/app-config/prospect_sources`, { headers: { Authorization: `Bearer ${token}` } });
        setSources(res.data?.sources || []);
      } catch (e) {}
    };
    load();
  }, []);
  return (
    <select value={value || ''} onChange={e => onChange(e.target.value)}
      className="w-full h-9 rounded-md border border-gray-200 bg-white px-3 text-sm">
      <option value="">-- Sélectionner --</option>
      {sources.map(s => <option key={s} value={s}>{s}</option>)}
    </select>
  );
};

const ClientForm = ({ client, onSave, onCancel, isEditing = false }) => {
  const [formData, setFormData] = useState(client || {
    // Informations personnelles
    nom: "",
    prenom: "",
    company_name: "",
    siren: "",
    etat_dossier: "nouveau",
    source_prospect: "",
    adresse: "",
    code_postal: "",
    ville: "",
    telephone_portable: "",
    telephone_fixe: "",
    email: "",
    
    // Dates RDV
    date_rdv: null,
    date_r2: null,
    date_signature: null,
    
    // SUNLIFE - Logement
    type_logement: "",
    annee_construction: "",
    surface_habitable: "",
    nombre_pieces: "",
    
    // Chauffage actuel
    mode_chauffage_actuel: "",
    type_chaudiere_actuelle: "",
    annee_chaudiere: "",
    
    // Isolation
    isolation_combles: false,
    isolation_murs: false,
    type_fenetres: "",
    
    // Aides MaPrimeRénov
    revenu_fiscal_reference: "",
    nombre_personnes_foyer: "",
    proprietaire: true,
    occupant_depuis: "",
    
    // Consommation énergétique
    facture_energie_annuelle: "",
    consommation_gaz_annuelle: "",
    consommation_elec_annuelle: "",
    dpe_actuel: "",
    ges_actuel: "",
    
    // Commercial - Assignation
    assigned_telepro: "",
    assigned_commercial: "",
    commission_telepro: "",
    commission_commercial: "",
    
    // Statut de suivi du pipeline
    statut_pipeline: "",
    date_signature_bdc: null,
    date_envoi_financement: null,
    date_depot_mairie: null,
    date_octroi_mpr: null,
    date_installation_programmee: null,
    date_installation: null,
    date_deblocage: null,
    date_paiement_final: null,
    
    // Commercial
    note_solvabilite: "",
    commentaires: "",
    telepro: false,
    apporteur: "",
    commission: "",
    status: "prospect",
    
    // 🆕 NOUVEAUX CHAMPS
    type_contrat_travail: "",
    domaine_activite: "",
    situation_familiale: "",
    montant_credit_en_cours: "",
    
    // 🆕 CHAMPS MONSIEUR/MADAME
    type_contrat_monsieur: "",
    type_contrat_madame: "",
    revenus_mensuels_monsieur: "",
    revenus_mensuels_madame: ""
  });

  const [documents, setDocuments] = useState({
    facture_energie: null,
    dpe: null,
    avis_imposition: null,
    piece_identite: null,
    justificatif_domicile: null
  });

  const [activeTab, setActiveTab] = useState("identite");
  const [users, setUsers] = useState([]);
  const [telepros, setTelepros] = useState([]);
  const [commercials, setCommercials] = useState([]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDateChange = (field, date) => {
    setFormData(prev => ({
      ...prev,
      [field]: date
    }));
  };

  const handleFileChange = (docType, file) => {
    setDocuments(prev => ({
      ...prev,
      [docType]: file
    }));
  };

  // Charger les utilisateurs (telepros et commerciaux)
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/users`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const allUsers = response.data;
        setUsers(allUsers);
        setTelepros(allUsers.filter(u => u.role === 'telepro'));
        setCommercials(allUsers.filter(u => u.role === 'commercial'));
      } catch (error) {
        console.error("Error fetching users:", error);
        toast.error("Erreur lors du chargement des utilisateurs");
      }
    };
    
    fetchUsers();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Format data for API
    const clientData = {
      ...formData,
      // Convertir les nombres
      surface_habitable: parseFloat(formData.surface_habitable) || null,
      nombre_pieces: parseInt(formData.nombre_pieces) || null,
      annee_construction: parseInt(formData.annee_construction) || null,
      annee_chaudiere: parseInt(formData.annee_chaudiere) || null,
      revenu_fiscal_reference: parseFloat(formData.revenu_fiscal_reference) || null,
      nombre_personnes_foyer: parseInt(formData.nombre_personnes_foyer) || null,
      occupant_depuis: parseInt(formData.occupant_depuis) || null,
      facture_energie_annuelle: parseFloat(formData.facture_energie_annuelle) || null,
      consommation_gaz_annuelle: parseFloat(formData.consommation_gaz_annuelle) || null,
      consommation_elec_annuelle: parseFloat(formData.consommation_elec_annuelle) || null,
      commission: parseFloat(formData.commission) || null,
      commission_telepro: parseFloat(formData.commission_telepro) || null,
      commission_commercial: parseFloat(formData.commission_commercial) || null,
      
      // Convert dates to ISO strings
      date_rdv: formData.date_rdv ? (formData.date_rdv instanceof Date ? formData.date_rdv.toISOString() : formData.date_rdv) : null,
      date_r2: formData.date_r2 ? (formData.date_r2 instanceof Date ? formData.date_r2.toISOString() : formData.date_r2) : null,
      date_signature: formData.date_signature ? (formData.date_signature instanceof Date ? formData.date_signature.toISOString() : formData.date_signature) : null,
      date_signature_bdc: formData.date_signature_bdc ? (formData.date_signature_bdc instanceof Date ? formData.date_signature_bdc.toISOString() : formData.date_signature_bdc) : null,
      date_envoi_financement: formData.date_envoi_financement ? (formData.date_envoi_financement instanceof Date ? formData.date_envoi_financement.toISOString() : formData.date_envoi_financement) : null,
      date_depot_mairie: formData.date_depot_mairie ? (formData.date_depot_mairie instanceof Date ? formData.date_depot_mairie.toISOString() : formData.date_depot_mairie) : null,
      date_octroi_mpr: formData.date_octroi_mpr ? (formData.date_octroi_mpr instanceof Date ? formData.date_octroi_mpr.toISOString() : formData.date_octroi_mpr) : null,
      date_installation_programmee: formData.date_installation_programmee ? (formData.date_installation_programmee instanceof Date ? formData.date_installation_programmee.toISOString() : formData.date_installation_programmee) : null,
      date_installation: formData.date_installation ? (formData.date_installation instanceof Date ? formData.date_installation.toISOString() : formData.date_installation) : null,
      date_deblocage: formData.date_deblocage ? (formData.date_deblocage instanceof Date ? formData.date_deblocage.toISOString() : formData.date_deblocage) : null,
      date_paiement_final: formData.date_paiement_final ? (formData.date_paiement_final instanceof Date ? formData.date_paiement_final.toISOString() : formData.date_paiement_final) : null,
    };

    onSave(clientData, documents);
  };

  const DatePickerField = ({ label, value, onChange, required = false }) => {
    // Normaliser la valeur en Date
    const dateValue = value ? (value instanceof Date ? value : new Date(value)) : null;
    const isValid = dateValue && !isNaN(dateValue.getTime());
    return (
      <div>
        <Label>{label} {required && "*"}</Label>
        <Popover modal={true}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-full justify-start text-left font-normal" type="button">
              <CalendarIcon className="mr-2 h-4 w-4" />
              {isValid ? format(dateValue, "dd/MM/yyyy", { locale: fr }) : "Sélectionner une date"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 z-[9999]" align="start" side="bottom" sideOffset={4}>
            <Calendar
              mode="single"
              selected={isValid ? dateValue : undefined}
              onSelect={onChange}
              initialFocus
              locale={fr}
            />
          </PopoverContent>
        </Popover>
      </div>
    );
  };

  const handleCompanySelect = (companyData) => {
    setFormData(prev => ({
      ...prev,
      siren: companyData.siren,
      company_name: companyData.raison_sociale,
      nom: companyData.nom || prev.nom,
      prenom: companyData.prenom || prev.prenom,
      adresse: companyData.adresse || "Adresse à compléter",
      code_postal: companyData.code_postal || "",
      ville: companyData.ville || "",
      commentaires: prev.commentaires ? 
        `${prev.commentaires}\nActivité: ${companyData.activite || ''}\nForme juridique: ${companyData.forme_juridique || ''}` :
        `Activité: ${companyData.activite || ''}\nForme juridique: ${companyData.forme_juridique || ''}`
    }));
    
    toast.success(`✅ Entreprise sélectionnée: ${companyData.raison_sociale}`);
  };

  const handleAddressSelect = (addressData) => {
    setFormData(prev => ({
      ...prev,
      adresse: addressData.adresse,
      code_postal: addressData.code_postal || "",
      ville: addressData.ville || ""
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Header avec status */}
      <div className="flex items-center justify-between border-b pb-4">
        <h2 className="text-2xl font-bold">
          {isEditing ? "Modifier" : "Créer"} {formData.status === "prospect" ? "un Prospect" : "un Client"}
        </h2>
        <Badge variant={formData.status === "client" ? "default" : "secondary"}>
          {formData.status === "prospect" ? "Prospect" : "Client"}
        </Badge>
      </div>

      {/* Tabs Navigation */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="identite" className="flex items-center gap-2">
            <User className="w-4 h-4" />
            Identité
          </TabsTrigger>
          <TabsTrigger value="logement" className="flex items-center gap-2">
            <Home className="w-4 h-4" />
            Logement
          </TabsTrigger>
          <TabsTrigger value="energie" className="flex items-center gap-2">
            <Zap className="w-4 h-4" />
            Énergie
          </TabsTrigger>
          <TabsTrigger value="aides" className="flex items-center gap-2">
            <Euro className="w-4 h-4" />
            Aides
          </TabsTrigger>
          <TabsTrigger value="commercial" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Commercial
          </TabsTrigger>
        </TabsList>

        {/* Tab 1: Identité */}
        <TabsContent value="identite" className="space-y-4 mt-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Recherche SIREN (Optionnel)
            </h3>
            <SirenSearch onCompanySelect={handleCompanySelect} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Nom *</Label>
              <Input
                value={formData.nom}
                onChange={(e) => handleInputChange("nom", e.target.value)}
                placeholder="Nom"
                required
              />
            </div>
            <div>
              <Label>Prénom *</Label>
              <Input
                value={formData.prenom}
                onChange={(e) => handleInputChange("prenom", e.target.value)}
                placeholder="Prénom"
                required
              />
            </div>
          </div>

          <div>
            <Label>Raison Sociale (si entreprise)</Label>
            <Input
              value={formData.company_name}
              onChange={(e) => handleInputChange("company_name", e.target.value)}
              placeholder="Nom de l'entreprise"
            />
          </div>

          <div>
            <Label>SIREN</Label>
            <Input
              value={formData.siren}
              onChange={(e) => handleInputChange("siren", e.target.value)}
              placeholder="123456789"
            />
          </div>

          <div className="bg-green-50 p-4 rounded-lg">
            <h3 className="font-semibold mb-3">Recherche Adresse</h3>
            <AddressSearch onAddressSelect={handleAddressSelect} />
          </div>

          <div>
            <Label>Adresse *</Label>
            <Input
              value={formData.adresse}
              onChange={(e) => handleInputChange("adresse", e.target.value)}
              placeholder="Numéro et rue"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Code Postal *</Label>
              <Input
                value={formData.code_postal}
                onChange={(e) => handleInputChange("code_postal", e.target.value)}
                placeholder="69003"
                required
              />
            </div>
            <div>
              <Label>Ville *</Label>
              <Input
                value={formData.ville}
                onChange={(e) => handleInputChange("ville", e.target.value)}
                placeholder="Lyon"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Téléphone Portable</Label>
              <Input
                type="tel"
                value={formData.telephone_portable}
                onChange={(e) => handleInputChange("telephone_portable", e.target.value)}
                placeholder="06 12 34 56 78"
              />
            </div>
            <div>
              <Label>Téléphone Fixe</Label>
              <Input
                type="tel"
                value={formData.telephone_fixe}
                onChange={(e) => handleInputChange("telephone_fixe", e.target.value)}
                placeholder="04 12 34 56 78"
              />
            </div>
          </div>

          <div>
            <Label>Email</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              placeholder="contact@exemple.fr"
            />
          </div>

          {/* 🆕 NOUVEAUX CHAMPS */}
          <div className="border-t pt-4 mt-4">
            <h4 className="font-semibold mb-3 text-gray-700">Informations Complémentaires</h4>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Situation Familiale</Label>
                <Select
                  value={formData.situation_familiale}
                  onValueChange={(value) => handleInputChange("situation_familiale", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="celibataire">Célibataire</SelectItem>
                    <SelectItem value="marie">Marié(e)</SelectItem>
                    <SelectItem value="pacse">Pacsé(e)</SelectItem>
                    <SelectItem value="divorce">Divorcé(e)</SelectItem>
                    <SelectItem value="veuf">Veuf(ve)</SelectItem>
                    <SelectItem value="concubinage">Concubinage</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Domaine d'Activité</Label>
                <Input
                  value={formData.domaine_activite}
                  onChange={(e) => handleInputChange("domaine_activite", e.target.value)}
                  placeholder="Ex: BTP, Commerce, Santé, etc."
                />
              </div>
            </div>

            {/* 🆕 SECTION MONSIEUR */}
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <h5 className="font-medium text-sm text-blue-900 mb-3">👨 Informations Monsieur</h5>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Type de Contrat</Label>
                  <Select
                    value={formData.type_contrat_monsieur}
                    onValueChange={(value) => handleInputChange("type_contrat_monsieur", value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cdi">CDI (Contrat Durée Indéterminée)</SelectItem>
                      <SelectItem value="cdd">CDD (Contrat Durée Déterminée)</SelectItem>
                      <SelectItem value="interim">Intérim</SelectItem>
                      <SelectItem value="independant">Indépendant / Auto-entrepreneur</SelectItem>
                      <SelectItem value="retraite">Retraité</SelectItem>
                      <SelectItem value="sans_emploi">Sans emploi</SelectItem>
                      <SelectItem value="etudiant">Étudiant</SelectItem>
                      <SelectItem value="autre">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Revenus Mensuels (€)</Label>
                  <Input
                    type="number"
                    value={formData.revenus_mensuels_monsieur}
                    onChange={(e) => handleInputChange("revenus_mensuels_monsieur", e.target.value)}
                    placeholder="0"
                    step="0.01"
                  />
                </div>
              </div>
            </div>

            {/* 🆕 SECTION MADAME */}
            <div className="mt-3 p-3 bg-pink-50 rounded-lg">
              <h5 className="font-medium text-sm text-pink-900 mb-3">👩 Informations Madame</h5>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Type de Contrat</Label>
                  <Select
                    value={formData.type_contrat_madame}
                    onValueChange={(value) => handleInputChange("type_contrat_madame", value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cdi">CDI (Contrat Durée Indéterminée)</SelectItem>
                      <SelectItem value="cdd">CDD (Contrat Durée Déterminée)</SelectItem>
                      <SelectItem value="interim">Intérim</SelectItem>
                      <SelectItem value="independant">Indépendant / Auto-entrepreneur</SelectItem>
                      <SelectItem value="retraite">Retraité</SelectItem>
                      <SelectItem value="sans_emploi">Sans emploi</SelectItem>
                      <SelectItem value="etudiant">Étudiant</SelectItem>
                      <SelectItem value="autre">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Revenus Mensuels (€)</Label>
                  <Input
                    type="number"
                    value={formData.revenus_mensuels_madame}
                    onChange={(e) => handleInputChange("revenus_mensuels_madame", e.target.value)}
                    placeholder="0"
                    step="0.01"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 mt-4">
              <div>
                <Label>Montant Crédit en Cours Total (€)</Label>
                <Input
                  type="number"
                  value={formData.montant_credit_en_cours}
                  onChange={(e) => handleInputChange("montant_credit_en_cours", e.target.value)}
                  placeholder="0"
                  step="0.01"
                />
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Tab 2: Logement */}
        <TabsContent value="logement" className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Type de Logement</Label>
              <Select
                value={formData.type_logement}
                onValueChange={(value) => handleInputChange("type_logement", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="maison">Maison individuelle</SelectItem>
                  <SelectItem value="appartement">Appartement</SelectItem>
                  <SelectItem value="immeuble">Immeuble</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Année de Construction</Label>
              <Input
                type="number"
                value={formData.annee_construction}
                onChange={(e) => handleInputChange("annee_construction", e.target.value)}
                placeholder="1990"
                min="1800"
                max={new Date().getFullYear()}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Surface Habitable (m²)</Label>
              <Input
                type="number"
                value={formData.surface_habitable}
                onChange={(e) => handleInputChange("surface_habitable", e.target.value)}
                placeholder="120"
                step="0.1"
              />
            </div>
            <div>
              <Label>Nombre de Pièces</Label>
              <Input
                type="number"
                value={formData.nombre_pieces}
                onChange={(e) => handleInputChange("nombre_pieces", e.target.value)}
                placeholder="5"
              />
            </div>
          </div>

          <div className="border-t pt-4">
            <h4 className="font-semibold mb-3">Isolation Actuelle</h4>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Isolation des Combles</Label>
                <Switch
                  checked={formData.isolation_combles}
                  onCheckedChange={(checked) => handleInputChange("isolation_combles", checked)}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label>Isolation des Murs</Label>
                <Switch
                  checked={formData.isolation_murs}
                  onCheckedChange={(checked) => handleInputChange("isolation_murs", checked)}
                />
              </div>
            </div>
          </div>

          <div>
            <Label>Type de Fenêtres</Label>
            <Select
              value={formData.type_fenetres}
              onValueChange={(value) => handleInputChange("type_fenetres", value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="simple_vitrage">Simple vitrage</SelectItem>
                <SelectItem value="double_vitrage">Double vitrage</SelectItem>
                <SelectItem value="triple_vitrage">Triple vitrage</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </TabsContent>

        {/* Tab 3: Énergie */}
        <TabsContent value="energie" className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Mode de Chauffage Actuel</Label>
              <Select
                value={formData.mode_chauffage_actuel}
                onValueChange={(value) => handleInputChange("mode_chauffage_actuel", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="gaz">Gaz</SelectItem>
                  <SelectItem value="electricite">Électricité</SelectItem>
                  <SelectItem value="fioul">Fioul</SelectItem>
                  <SelectItem value="bois">Bois</SelectItem>
                  <SelectItem value="pac">Pompe à chaleur</SelectItem>
                  <SelectItem value="autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Type de Chaudière</Label>
              <Input
                value={formData.type_chaudiere_actuelle}
                onChange={(e) => handleInputChange("type_chaudiere_actuelle", e.target.value)}
                placeholder="Ex: Chaudière gaz condensation"
              />
            </div>
          </div>

          <div>
            <Label>Année de la Chaudière</Label>
            <Input
              type="number"
              value={formData.annee_chaudiere}
              onChange={(e) => handleInputChange("annee_chaudiere", e.target.value)}
              placeholder="2010"
              min="1950"
              max={new Date().getFullYear()}
            />
          </div>

          <div className="border-t pt-4">
            <h4 className="font-semibold mb-3">Consommation Énergétique</h4>
            <div className="space-y-4">
              <div>
                <Label>Facture Énergie Annuelle (€)</Label>
                <Input
                  type="number"
                  value={formData.facture_energie_annuelle}
                  onChange={(e) => handleInputChange("facture_energie_annuelle", e.target.value)}
                  placeholder="2500"
                  step="0.01"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Consommation Gaz Annuelle (kWh)</Label>
                  <Input
                    type="number"
                    value={formData.consommation_gaz_annuelle}
                    onChange={(e) => handleInputChange("consommation_gaz_annuelle", e.target.value)}
                    placeholder="15000"
                  />
                </div>
                <div>
                  <Label>Consommation Élec Annuelle (kWh)</Label>
                  <Input
                    type="number"
                    value={formData.consommation_elec_annuelle}
                    onChange={(e) => handleInputChange("consommation_elec_annuelle", e.target.value)}
                    placeholder="8000"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>DPE Actuel</Label>
              <Select
                value={formData.dpe_actuel}
                onValueChange={(value) => handleInputChange("dpe_actuel", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="A">A (Excellent)</SelectItem>
                  <SelectItem value="B">B (Très bon)</SelectItem>
                  <SelectItem value="C">C (Bon)</SelectItem>
                  <SelectItem value="D">D (Moyen)</SelectItem>
                  <SelectItem value="E">E (Passable)</SelectItem>
                  <SelectItem value="F">F (Médiocre)</SelectItem>
                  <SelectItem value="G">G (Très médiocre)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>GES (Gaz à Effet de Serre)</Label>
              <Select
                value={formData.ges_actuel}
                onValueChange={(value) => handleInputChange("ges_actuel", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="A">A</SelectItem>
                  <SelectItem value="B">B</SelectItem>
                  <SelectItem value="C">C</SelectItem>
                  <SelectItem value="D">D</SelectItem>
                  <SelectItem value="E">E</SelectItem>
                  <SelectItem value="F">F</SelectItem>
                  <SelectItem value="G">G</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </TabsContent>

        {/* Tab 4: Aides */}
        <TabsContent value="aides" className="space-y-4 mt-4">
          <div className="bg-yellow-50 p-4 rounded-lg mb-4">
            <h4 className="font-semibold mb-2">Éligibilité MaPrimeRénov & CEE</h4>
            <p className="text-sm text-gray-600">
              Ces informations permettent de calculer l'éligibilité aux aides de l'État
            </p>
          </div>

          <div>
            <Label>Revenu Fiscal de Référence (€)</Label>
            <Input
              type="number"
              value={formData.revenu_fiscal_reference}
              onChange={(e) => handleInputChange("revenu_fiscal_reference", e.target.value)}
              placeholder="45000"
              step="0.01"
            />
          </div>

          <div>
            <Label>Nombre de Personnes dans le Foyer</Label>
            <Input
              type="number"
              value={formData.nombre_personnes_foyer}
              onChange={(e) => handleInputChange("nombre_personnes_foyer", e.target.value)}
              placeholder="4"
              min="1"
            />
          </div>

          <div className="flex items-center justify-between py-3 border-y">
            <Label>Propriétaire du Logement</Label>
            <Switch
              checked={formData.proprietaire}
              onCheckedChange={(checked) => handleInputChange("proprietaire", checked)}
            />
          </div>

          <div>
            <Label>Occupant Depuis (années)</Label>
            <Input
              type="number"
              value={formData.occupant_depuis}
              onChange={(e) => handleInputChange("occupant_depuis", e.target.value)}
              placeholder="10"
              min="0"
            />
          </div>
        </TabsContent>

        {/* Tab 5: Commercial */}
        <TabsContent value="commercial" className="space-y-4 mt-4">
          <div className="bg-blue-50 p-4 rounded-lg mb-4">
            <h4 className="font-semibold mb-2">Assignation & Commissions</h4>
            <p className="text-sm text-gray-600">
              Assigner le prospect/client à un téléprospecteur et/ou commercial
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Téléprospecteur Assigné</Label>
              <Select
                value={formData.assigned_telepro}
                onValueChange={(value) => handleInputChange("assigned_telepro", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un telepro" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun</SelectItem>
                  {telepros.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.first_name} {user.last_name} ({user.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Commission Telepro (€)</Label>
              <Input
                type="number"
                value={formData.commission_telepro}
                onChange={(e) => handleInputChange("commission_telepro", e.target.value)}
                placeholder="150"
                step="0.01"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Commercial Assigné</Label>
              <Select
                value={formData.assigned_commercial}
                onValueChange={(value) => handleInputChange("assigned_commercial", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un commercial" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun</SelectItem>
                  {commercials.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.first_name} {user.last_name} ({user.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Commission Commercial (€)</Label>
              <Input
                type="number"
                value={formData.commission_commercial}
                onChange={(e) => handleInputChange("commission_commercial", e.target.value)}
                placeholder="500"
                step="0.01"
              />
            </div>
          </div>

          <div className="border-t pt-4">
            <h4 className="font-semibold mb-3">État et Statut</h4>
          </div>

          <div>
            <Label>État du Dossier</Label>
            <Select
              value={formData.etat_dossier}
              onValueChange={(value) => handleInputChange("etat_dossier", value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="nouveau">Nouveau</SelectItem>
                <SelectItem value="en_cours">En cours</SelectItem>
                <SelectItem value="signe">Signé</SelectItem>
                <SelectItem value="refuse">Refusé</SelectItem>
                <SelectItem value="attente">En attente</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Statut (Prospect → Client)</Label>
            <Select
              value={formData.status}
              onValueChange={(value) => handleInputChange("status", value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="prospect">Prospect</SelectItem>
                <SelectItem value="client">Client</SelectItem>
                <SelectItem value="lost">Perdu</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="border-t pt-4">
            <h4 className="font-semibold mb-3 flex items-center gap-2">
              📊 Suivi du Pipeline (Client signé uniquement)
            </h4>
            <p className="text-sm text-gray-600 mb-3">
              Suivre l'avancement du projet d'installation du client
            </p>
          </div>

          <div>
            <Label>Statut Pipeline</Label>
            <Select
              value={formData.statut_pipeline || "none"}
              onValueChange={(value) => handleInputChange("statut_pipeline", value === "none" ? "" : value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner un statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">-- Aucun --</SelectItem>
                <SelectItem value="bdc_signe">✅ BDC Signé</SelectItem>
                <SelectItem value="envoi_financement">💳 Envoyé en Financement</SelectItem>
                <SelectItem value="attente_financement">⏳ Attente Accord Financement</SelectItem>
                <SelectItem value="depot_mairie">🏛️ Dépôt Dossier Mairie</SelectItem>
                <SelectItem value="attente_mairie">⏳ Attente Autorisation Mairie</SelectItem>
                <SelectItem value="demande_mpr">📄 Demande MaPrimeRénov</SelectItem>
                <SelectItem value="attente_octroi_mpr">⏳ Attente Octroi MPR</SelectItem>
                <SelectItem value="mpr_accordee">✅ MPR Accordée</SelectItem>
                <SelectItem value="a_programmer">📅 À Programmer en Installation</SelectItem>
                <SelectItem value="installation_programmee">🔧 Installation Programmée</SelectItem>
                <SelectItem value="en_installation">🚧 Installation en Cours</SelectItem>
                <SelectItem value="installation_terminee">✅ Installation Terminée</SelectItem>
                <SelectItem value="attente_deblocage">💰 Attente Déblocage Fonds</SelectItem>
                <SelectItem value="paiement_partiel">💵 Paiement Partiel Reçu</SelectItem>
                <SelectItem value="paye_finalise">✅ Client Payé et Finalisé</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Source Prospect</Label>
            <SourceProspectSelect value={formData.source_prospect} onChange={(v) => handleInputChange("source_prospect", v)} />
          </div>

          <div className="bg-gray-50 p-4 rounded-lg space-y-3">
            <h5 className="font-semibold text-sm">Dates du Pipeline</h5>
            <div className="grid grid-cols-2 gap-3">
              <DatePickerField
                label="BDC Signé"
                value={formData.date_signature_bdc}
                onChange={(date) => handleDateChange("date_signature_bdc", date)}
              />
              <DatePickerField
                label="Envoi Financement"
                value={formData.date_envoi_financement}
                onChange={(date) => handleDateChange("date_envoi_financement", date)}
              />
              <DatePickerField
                label="Dépôt Mairie"
                value={formData.date_depot_mairie}
                onChange={(date) => handleDateChange("date_depot_mairie", date)}
              />
              <DatePickerField
                label="Octroi MPR"
                value={formData.date_octroi_mpr}
                onChange={(date) => handleDateChange("date_octroi_mpr", date)}
              />
              <DatePickerField
                label="Installation Programmée"
                value={formData.date_installation_programmee}
                onChange={(date) => handleDateChange("date_installation_programmee", date)}
              />
              <DatePickerField
                label="Date Installation Réalisée"
                value={formData.date_installation}
                onChange={(date) => handleDateChange("date_installation", date)}
              />
              <DatePickerField
                label="Déblocage Fonds"
                value={formData.date_deblocage}
                onChange={(date) => handleDateChange("date_deblocage", date)}
              />
              <DatePickerField
                label="Paiement Final"
                value={formData.date_paiement_final}
                onChange={(date) => handleDateChange("date_paiement_final", date)}
              />
            </div>
          </div>

          <div className="border-t pt-4">
            <h4 className="font-semibold mb-3">RDV</h4>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <DatePickerField
              label="Date RDV"
              value={formData.date_rdv}
              onChange={(date) => handleDateChange("date_rdv", date)}
            />
            <DatePickerField
              label="Date R2"
              value={formData.date_r2}
              onChange={(date) => handleDateChange("date_r2", date)}
            />
            <DatePickerField
              label="Date Signature"
              value={formData.date_signature}
              onChange={(date) => handleDateChange("date_signature", date)}
            />
          </div>

          <div>
            <Label>Solvabilité</Label>
            <Select
              value={formData.note_solvabilite}
              onValueChange={(value) => handleInputChange("note_solvabilite", value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Évaluer" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="excellent">Excellent</SelectItem>
                <SelectItem value="bon">Bon</SelectItem>
                <SelectItem value="moyen">Moyen</SelectItem>
                <SelectItem value="faible">Faible</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between py-3 border-y">
            <Label>Prospect Téléprospecteur</Label>
            <Switch
              checked={formData.telepro}
              onCheckedChange={(checked) => handleInputChange("telepro", checked)}
            />
          </div>

          <div>
            <Label>Apporteur d'Affaires</Label>
            <Input
              value={formData.apporteur}
              onChange={(e) => handleInputChange("apporteur", e.target.value)}
              placeholder="Nom de l'apporteur"
            />
          </div>

          <div>
            <Label>Commission (€)</Label>
            <Input
              type="number"
              value={formData.commission}
              onChange={(e) => handleInputChange("commission", e.target.value)}
              placeholder="500"
              step="0.01"
            />
          </div>

          <div>
            <Label>Commentaires</Label>
            <Textarea
              value={formData.commentaires}
              onChange={(e) => handleInputChange("commentaires", e.target.value)}
              placeholder="Notes additionnelles..."
              rows={4}
            />
          </div>
        </TabsContent>
      </Tabs>

      {/* Footer Buttons */}
      <div className="flex justify-end gap-3 border-t pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Annuler
        </Button>
        <Button type="submit">
          {isEditing ? "Mettre à jour" : "Créer"}
        </Button>
      </div>
    </form>
  );
};

export default ClientForm;
